import { cn } from '@/lib/cn'

/**
 * The CrystalDEX mark: a faceted crystal "C" with a chrome inner bevel, as drawn in the
 * brand deck. The same vector ships as public/brand/mark.svg for the admin and favicons.
 */
/**
 * The mark's path, and the exit terminal that sits at the end of it.
 *
 * Exported because the loading indicator draws this same figure — see ui/loading-indicator.tsx,
 * where the pipe is stroked on progressively to show capital moving through the route. Two
 * copies of this data would drift the first time the monogram is retuned, and the drift would
 * be invisible: a logo and a loader that disagree by two units still each look deliberate.
 *
 * The loader keeps this simpler routed line: a pipe that strokes on reads as motion, where
 * the faceted crystal would only fade.
 */
export const MARK_PATH_D = 'M48 48 L31 55 L15 45 L15 19 L31 9 L48 16'
export const MARK_NODE = { cx: 48, cy: 16, r: 5.5 } as const

const POINTS = {
  A: [56, 0], B: [100, 26], C: [80, 48], D: [52, 32], E: [28, 64], F: [52, 96], G: [80, 80],
  H: [100, 102], I: [56, 128], J: [0, 96], K: [0, 32], P: [72, 25], Q: [72, 103], R: [14, 64],
} as const
type Key = keyof typeof POINTS

/** Cut faces as [vertices, shade]; shade 0 is the deep blue, 1 the lit facet. */
const FACETS: [Key[], number][] = [
  [['A', 'B', 'P'], 0.95], [['B', 'C', 'P'], 0.45], [['C', 'D', 'P'], 0.3], [['D', 'A', 'P'], 0.75],
  [['A', 'D', 'R'], 0.85], [['K', 'A', 'R'], 0.65], [['D', 'E', 'R'], 0.4], [['J', 'K', 'R'], 0.3],
  [['E', 'F', 'R'], 0.22], [['F', 'I', 'R'], 0.35], [['I', 'J', 'R'], 0.12],
  [['F', 'G', 'Q'], 0.7], [['G', 'H', 'Q'], 0.4], [['H', 'I', 'Q'], 0.15], [['I', 'F', 'Q'], 0.5],
]

const shade = (t: number) =>
  `rgb(${Math.round(10 + 86 * t)} ${Math.round(34 + 116 * t)} ${Math.round(110 + 140 * t)})`
const pts = (keys: Key[]) => keys.map((k) => POINTS[k].join(',')).join(' ')

function Crystal({ className }: { className?: string }) {
  return (
    <svg viewBox="-4 -4 108 136" aria-hidden className={className}>
      <defs>
        <linearGradient id="brand-chrome" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ffffff" />
          <stop offset="0.45" stopColor="#b9c6dc" />
          <stop offset="0.55" stopColor="#f4f7fc" />
          <stop offset="1" stopColor="#8c9ab4" />
        </linearGradient>
      </defs>
      {FACETS.map(([keys, t]) => (
        <polygon key={keys.join('')} points={pts(keys)} fill={shade(t)} stroke="rgba(255,255,255,0.28)" strokeWidth="0.6" strokeLinejoin="round" />
      ))}
      <polyline points={pts(['D', 'E', 'F'])} fill="none" stroke="url(#brand-chrome)" strokeWidth="4.2" />
    </svg>
  )
}

export function BrandMark({ className }: { className?: string }) {
  return <Crystal className={className} />
}

/**
 * The mark on its own square ground — used wherever a fixed-size icon slot needs filling
 * (rail header, footer). The crystal is inset so its points never touch the slot's edge.
 */
export function BrandTile({ className }: { className?: string }) {
  return (
    <span className={cn('grid place-items-center overflow-hidden', className)}>
      <Crystal className="h-[86%] w-auto" />
    </span>
  )
}
